<template>
  <div class="customer-selector">
    <!-- Selected Customer Display -->
    <CustomerDisplay
      v-if="props.modelValue"
      :customer="props.modelValue"
      :order-type="props.orderType"
      :customer-addresses="customerAddresses"
      :selected-address-id="selectedAddressId"
      @clear-customer="handleClearCustomer"
      @address-selected="handleAddressSelected"
      @show-address-manager="showAddressManager = true"
    />

    <!-- Customer Selection Interface -->
    <div
      v-else
      class="space-y-4"
    >
      <!-- Customer Search -->
      <CustomerSearch
        :search-query="searchQuery"
        :search-results="searchResults"
        :is-searching="isSearching"
        :show-create-form="showCreateForm"
        @update:search-query="searchQuery = $event"
        @search="handleSearch"
        @select-customer="handleCustomerSelect"
        @show-create-form="showCreateForm = $event"
      />

      <!-- Recent Customers -->
      <CustomerRecentList
        :recent-customers="recentCustomers"
        @select-customer="handleCustomerSelect"
      />
    </div>

    <!-- Create Customer Form (shown when create mode is active) -->
    <CustomerCreateForm
      v-if="showCreateForm"
      @saved="handleCustomerCreated"
      @cancel="showCreateForm = false"
    />

    <!-- Address Manager Modal -->
    <AddressManager
      v-if="showAddressManager && props.modelValue"
      :customer-id="props.modelValue.id"
      :selected-address-id="selectedAddressId"
      @address-selected="handleAddressSelected"
      @address-updated="loadCustomerAddresses(props.modelValue!.id)"
      @close="showAddressManager = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useCustomerSearch } from '@/composables/useCustomerSearch';
import { useCustomerAddresses } from '@/composables/useCustomerAddresses';
import { useCustomerSelection } from '@/composables/useCustomerSelection';
import AddressManager from './AddressManager.vue';
import CustomerCreateForm from './CustomerCreateForm.vue';
import CustomerDisplay from './CustomerDisplay.vue';
import CustomerSearch from './CustomerSearch.vue';
import CustomerRecentList from './CustomerRecentList.vue';
import type { Customer, CustomerAddress } from '@/types/customer';

interface Props {
  modelValue: Customer | null;
  orderType: 'Delivery' | 'Takeaway';
  initialSelectedAddressId?: number | null;
}

interface Emits {
  'update:modelValue': [customer: Customer | null];
  'create-customer': [];
  'address-selected': [address: CustomerAddress | null];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Use composables
const { searchQuery, searchResults, isSearching, handleSearch, clearSearch } = useCustomerSearch();
const { 
  customerAddresses, 
  selectedAddressId, 
  loadCustomerAddresses, 
  selectAddress, 
  clearAddresses,
  shouldShowAddresses 
} = useCustomerAddresses();
const { recentCustomers, loadRecentCustomers, refreshRecentCustomers } = useCustomerSelection();

// Local state
const showCreateForm = ref(false);
const showAddressManager = ref(false);

// Event handlers
const handleCustomerSelect = (customer: Customer) => {
  emit('update:modelValue', customer);
  clearSearch();
  showCreateForm.value = false;
};

const handleAddressSelected = (address: CustomerAddress) => {
  const selected = selectAddress(address);
  emit('address-selected', selected);
};

const handleClearCustomer = () => {
  emit('update:modelValue', null);
};

const handleCustomerCreated = (customer: Customer) => {
  handleCustomerSelect(customer);
  refreshRecentCustomers();
};

// Watch for customer value changes
watch(() => props.modelValue, (newValue, oldValue) => {
  // Only proceed if the value actually changed and it's not the same customer
  if (newValue?.id !== oldValue?.id) {
    if (newValue) {
      // Load customer addresses
      loadCustomerAddresses(newValue.id, props.initialSelectedAddressId);
    } else {
      clearAddresses();
    }
  }
}, { immediate: true });

// Watch for initial selected address (for persistence across re-renders)
watch(() => props.initialSelectedAddressId, (newAddressId, oldAddressId) => {
  if (newAddressId && props.modelValue) {
    // When we have an initial selected address ID, use it
    selectedAddressId.value = newAddressId;
  } else if (!newAddressId && oldAddressId && props.modelValue) {
    // When the address is cleared (set to null), but we still have a customer
    // Don't clear the selection if the user already has one selected
    // This prevents resetting the user's manual selection during re-renders
    if (!selectedAddressId.value) {
      // Only clear if no address is currently selected
      selectedAddressId.value = undefined;
    }
  }
}, { immediate: true });

// Initialize
onMounted(() => {
  loadRecentCustomers();
});
</script>

<style scoped>
.customer-selector {
  max-width: 600px;
  width: 100%;
}

/* Mobile-specific styles */
@media (max-width: 768px) {
  .customer-selector {
    max-width: 100%;
    width: 100%;
    padding: 0;
    margin-top: 1rem;
  }
  
  /* Ensure no horizontal overflow */
  .customer-selector * {
    max-width: 100%;
    box-sizing: border-box;
  }
  
  /* Better touch targets on mobile */
  .customer-selector button:not(.absolute) {
    min-height: 44px; /* iOS recommended minimum touch target */
    width: 100%;
  }
  
  /* Keep absolute positioned buttons at their original size */
  .customer-selector button.absolute {
    min-height: auto;
    width: auto;
  }
  
  .customer-selector input,
  .customer-selector textarea {
    font-size: 16px; /* Prevents zoom on iOS */
    width: 100%;
  }
  
  /* Improve spacing on mobile */
  .customer-selector .space-y-4 > * + * {
    margin-top: 1rem;
  }
  
  /* Better scroll on mobile */
  .customer-selector .overflow-y-auto {
    -webkit-overflow-scrolling: touch;
  }
  
  /* Fix flex layouts on mobile */
  .customer-selector .flex {
    flex-wrap: wrap;
  }
  
  .customer-selector .flex-col {
    width: 100%;
  }
  
  /* Adjust button layouts */
  .customer-selector .sm\\:flex-row {
    flex-direction: column !important;
  }
  
  .customer-selector .sm\\:space-x-2 > * + * {
    margin-left: 0 !important;
    margin-top: 0.5rem;
  }
  
  /* Fix text overflow */
  .customer-selector .truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  /* Adjust padding for mobile */
  .customer-selector .p-4 {
    padding: 1rem 0.75rem;
  }
  
  .customer-selector .p-3 {
    padding: 0.75rem;
  }
  
  /* Fix grid layouts */
  .customer-selector .md\\:grid-cols-2 {
    grid-template-columns: 1fr !important;
  }
  
  .customer-selector .md\\:col-span-2 {
    grid-column: span 1 !important;
  }
}

/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Dark mode scrollbar */
@media (prefers-color-scheme: dark) {
  .overflow-y-auto::-webkit-scrollbar-track {
    background: #374151;
  }
  
  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #6b7280;
  }
  
  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
}

/* Active states for better mobile feedback */
.customer-selector button:active {
  transform: scale(0.98);
}

.customer-selector [role="button"]:active {
  transform: scale(0.98);
}
</style>
